import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Log error for monitoring (can be extended to send to Sentry, etc.)
    this.logError(error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private logError(error: Error, errorInfo: ErrorInfo) {
    // For now, log to console with structured data
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    };

    // Store in localStorage for debugging (optional, can be removed in production)
    try {
      const recentErrors = JSON.parse(localStorage.getItem('recentErrors') || '[]');
      recentErrors.unshift(errorData);
      recentErrors.splice(10); // Keep only last 10 errors
      localStorage.setItem('recentErrors', JSON.stringify(recentErrors));
    } catch {
      // Ignore localStorage errors
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error } = this.state;
      const isNetworkError =
        error?.message?.includes('Network') || error?.message?.includes('fetch');
      const isValidationError =
        error?.name === 'ZodError' || error?.message?.includes('validation');

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                {isNetworkError
                  ? 'We encountered a network error. Please check your connection and try again.'
                  : isValidationError
                    ? 'There was a validation error. Please check your input and try again.'
                    : 'An unexpected error occurred. Our team has been notified.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <div className="rounded-md bg-muted p-4">
                  <p className="text-sm font-medium mb-2">Error Details:</p>
                  <pre className="text-xs overflow-auto max-h-48 text-muted-foreground">
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button onClick={this.handleReset} variant="default" className="w-full">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
                <Button onClick={this.handleReload} variant="outline" className="w-full">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reload Page
                </Button>
                <Button onClick={this.handleGoHome} variant="ghost" className="w-full">
                  <Home className="mr-2 h-4 w-4" />
                  Go to Home
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground text-center">
                If this problem persists, please contact support.
              </p>
              <Button
                variant="link"
                onClick={() => {
                  window.location.href = `mailto:support@example.com?subject=Error Report&body=${encodeURIComponent(
                    `Error: ${error?.message}\n\nPlease describe what you were doing when this error occurred:`,
                  )}`;
                }}
                className="text-sm"
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact Support
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
